import { type SearchRecordsStep } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/actionSectionTypes';
import { TFunction } from 'react-i18next';
import DraggableStepCard, { DraggableStepCardProps } from '../../DraggableStepCard';
import { getValueWithDeleted } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/SearchRecords/helpers';
import { SearchRecordsDetails } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/SearchRecords/types';
import { filterText } from 'pages/AutomationEngine/hooks/useAddFilterDescriptions';
import { CONFLICT_RESOLUTIONS } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/SearchRecords/constants';

export const getSearchDescription = (
  details: SearchRecordsDetails,
  t: TFunction
) => {
  if (details.filterType === 'all_records') {
    return t('is All Records');
  }
  if (details.filterType === 'custom_filter') {
    return filterText(details.filterConfig);
  }

  const groups = details.filterGroups || [];
  const groupNames = groups.length
    ? groups
        .map(
          ({ name, deleted }) =>
            `‘${name || '—'}${deleted ? ` [${t('Deleted')}]` : ''}’`
        )
        .join(', ')
    : t("'[Deleted]'");

  return details.filterType === 'in_group'
    ? `${t('In Group(s)')} ${groupNames}`
    : `${t('Not In Group(s)')} ${groupNames}`;
};

export const content = ({ step }: { step: SearchRecordsStep }) =>
  step.description;

export const getDescription = ({
  step,
  t,
}: {
  step: SearchRecordsStep;
  t: TFunction;
}) => {
  const entityName = step.details.customObject?.data?.entityName
    ? [
        step.details.customObject.data.entityName,
        step.details.customObject.deleted && `[${t('Deleted')}]`,
      ]
        .filter(Boolean)
        .join(' ')
    : `[${t('Deleted')}]`;

  const variableName =
    getValueWithDeleted(step.details.destinationVariable, t)?.label ||
    `[${t('Deleted')}]`;

  switch (step.details.destinationVariableResolution) {
    case CONFLICT_RESOLUTIONS.update_if_blank:
    case CONFLICT_RESOLUTIONS.add_only:
      return t(
        "Add Items to Array '{{ variableName }}' Where {{ entityName }} {{ search }}",
        {
          variableName,
          entityName,
          search: getSearchDescription(step.details, t),
        }
      );
    case CONFLICT_RESOLUTIONS.overwrite:
    case CONFLICT_RESOLUTIONS.overwrite_except_null:
      return t(
        "Overwrite Array '{{ variableName }}' Where {{ entityName }} {{ search }}",
        {
          variableName,
          entityName,
          search: getSearchDescription(step.details, t),
        }
      );
    case CONFLICT_RESOLUTIONS.remove_only:
      return t(
        "Remove Items from Array '{{ variableName }}' Where {{ entityName }} {{ search }}",
        {
          variableName,
          entityName,
          search: getSearchDescription(step.details, t),
        }
      );
    default:
      return '';
  }
};

export default function SearchRecordsCard({
  step,
  ...others
}: {
  step: SearchRecordsStep;
} & DraggableStepCardProps) {
  return <DraggableStepCard {...others}>{content({ step })}</DraggableStepCard>;
}
